import { useEffect, useRef, useState } from "react";

// IntersectionObserver-based reveal with a fallback timer so content
// can NEVER stay hidden even if the observer never fires.
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fallback = setTimeout(() => setInView(true), 900);

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return () => clearTimeout(fallback);
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            clearTimeout(fallback);
            io.disconnect();
          }
        });
      },
      { threshold }
    );
    io.observe(el);
    return () => {
      clearTimeout(fallback);
      io.disconnect();
    };
  }, [threshold]);

  return [ref, inView];
}

// Masked line-by-line reveal on load (pure CSS, always completes).
export const RevealLines = ({ lines = [], className = "", delay = 0, stagger = 0.12 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
        <span className="v-line" style={{ animationDelay: `${delay + i * stagger}s` }}>
          {line}
        </span>
      </span>
    ))}
  </span>
);

// Scroll-triggered fade + rise for any block of content.
export const Reveal = ({ children, className = "", delay = 0 }) => {
  const [ref, inView] = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${inView ? "in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

// Word-by-word masked reveal on scroll into view.
export const RevealWordsOnScroll = ({ text, className = "", stagger = 0.05 }) => {
  const [ref, inView] = useReveal(0.2);
  const words = text.split(" ");
  return (
    <span ref={ref} className={`${inView ? "in" : ""} ${className}`}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom mr-[0.25em] pb-[0.12em] -mb-[0.12em]">
          <span className="reveal-word" style={{ transitionDelay: `${i * stagger}s` }}>
            {w}
          </span>
        </span>
      ))}
    </span>
  );
};
